import { Modal } from "@/ui/modals";
import FilterRow from "./filter-row";
import { Button } from "@/ui/button";
import Input from "@/ui/input";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import { InputIcon } from "@/ui/min-max-input";
import { useEffect, useState } from "react";
import { WalletPosition } from "@/api/positions/types";
import { useBotTransferNativeMutation } from "@/api/bot/bot-transfer-native";
import { useBotTransferTokenMutation } from "@/api/bot/bot-transfer-token";
import { useWallet } from "@/providers/wallet";
import { formatNativeCurrency, formatTokenCurrency } from "@/utils/currency";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import Avatar from "@/ui/avatar";
import { useQueryClient } from "@tanstack/react-query";
import { POSITIONS_QUERY_KEY } from "@/api/positions/positions-bot";

const TokenSendModal = ({
  isOpen,
  toggleOpen,
  position,
}: {
  isOpen: boolean;
  toggleOpen: () => void;
  position: WalletPosition | null;
}) => {
  const { isConnected } = useWeb3Auth();
  const {
    mutate: transferNative,
    isPending: isPendingNative,
    isSuccess: isSuccessNative,
  } = useBotTransferNativeMutation();
  const {
    mutate: transferToken,
    isPending,
    isSuccess,
  } = useBotTransferTokenMutation();

  const { appPubKey, userId, currentWallet } = useWallet();

  const queryClient = useQueryClient();

  const MAX = position?.balance ?? 0;
  const tokenAddress = position?.token_address;
  const tokenDecimals = position?.decimals;

  const [sendAmount, setSendAmount] = useState({
    amount: "",
    percentage: "",
    wallet: "",
  });

  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSendAmount({
        amount: "",
        percentage: "",
        wallet: "",
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSuccess || isSuccessNative) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
  }, [isSuccess, isSuccessNative]);

  const inputRightSide = (showPercentage = false, onAllClick: () => void) => {
    return (
      <div className="flex flex-shrink-0 h-[1.25rem]">
        <button
          onClick={onAllClick}
          className="text-xs px-1 text-white font-normal text-opacity-50 hover:text-opacity-100"
        >
          All
        </button>
        {showPercentage && (
          <GradientBorderRadius
            radius="left"
            gradient="input-50"
            className="flex items-center"
          >
            <div className="p-1">
              <span className="px-0.5 text-xs text-white font-normal">%</span>
            </div>
          </GradientBorderRadius>
        )}
      </div>
    );
  };

  // For sending native tokens (ETH, SOL, etc)
  const handleSendNative = () => {
    if (sendAmount.amount === "" || sendAmount.wallet === "") {
      return;
    }

    transferNative(
      {
        params: {
          appPubKey,
          to: sendAmount.wallet,
          amount: formatNativeCurrency(
            sendAmount?.amount,
            currentWallet?.botChainId
          ),
          walletAddress: currentWallet.address,
          userId,
          chainId: currentWallet.botChainId,
        },
        notifyParams: {
          amount: sendAmount.amount,
          tokenSymbol: position?.token_symbol || "",
          walletAddress: sendAmount.wallet,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [POSITIONS_QUERY_KEY],
            exact: false,
          });
        },
      }
    );
  };

  // For sending tokens
  const handleSendToken = () => {
    if (sendAmount.amount === "" || sendAmount.wallet === "") {
      return;
    }

    transferToken(
      {
        params: {
          appPubKey,
          to: sendAmount.wallet,
          amount: formatTokenCurrency(sendAmount?.amount, tokenDecimals || 18),
          walletAddress: currentWallet.address,
          userId,
          tokenAddress: tokenAddress || "",
          chainId: currentWallet.botChainId,
        },
        notifyParams: {
          amount: sendAmount.amount,
          tokenSymbol: position?.token_symbol || "",
          walletAddress: sendAmount.wallet,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [POSITIONS_QUERY_KEY],
            exact: false,
          });
        },
      }
    );
  };

  const handleSend = () => {
    if (tokenAddress !== "native") {
      handleSendToken();
    } else {
      handleSendNative();
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={() => toggleOpen()}
      className="py-[0.9375rem] px-[0.6250rem] bg-section-gradient"
    >
      <Modal.Title title="Send" className="text-xs font-semibold" />
      <div className="w-full h-full grid grid-cols-[max-content,1fr] gap-y-[0.375rem] gap-x-[0.625rem] text-white text-xs font-semibold">
        <FilterRow title="Send">
          <div
            className={`gap-[0.3125rem] w-full h-full placeholder:font-normal placeholder:text-gray-400  placeholder:text-xs  grid grid-cols-2`}
          >
            <div className="group h-[1.375rem]">
              <Input
                value={sendAmount.amount}
                leftSide={
                  <GradientBorderRadius
                    radius="right"
                    gradient="input-50-min"
                    hover="input-50-left"
                    className="h-full w-[1.88rem]"
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      <Avatar source={position?.logo ?? ""} />
                    </div>
                  </GradientBorderRadius>
                }
                rightSide={inputRightSide(false, () =>
                  setSendAmount((sAmount) => ({
                    ...sAmount,
                    amount: `${MAX}`,
                    percentage: "100",
                  }))
                )}
                placeholder="Amount"
                onChange={(amount) => {
                  if ((Number(amount) / MAX) * 100 <= 100) {
                    setSendAmount((sAmount) => ({
                      ...sAmount,
                      amount,
                      percentage:
                        amount !== "" ? `${(Number(amount) / MAX) * 100}` : "",
                    }));
                  }
                }}
                className="h-[1.375rem] border-none pl-[0.313rem]"
                type="number"
              />
            </div>
            <div className="group h-[1.375rem]">
              <Input
                value={sendAmount.percentage}
                rightSide={inputRightSide(true, () =>
                  setSendAmount((sAmount) => ({
                    ...sAmount,
                    amount: `${MAX}`,
                    percentage: "100",
                  }))
                )}
                placeholder="Amount"
                onChange={(percentage) => {
                  if (Number(percentage) <= 100) {
                    setSendAmount((sAmount) => ({
                      ...sAmount,
                      amount:
                        percentage !== ""
                          ? `${(Number(percentage) / 100) * MAX}`
                          : "",
                      percentage,
                    }));
                  }
                }}
                className="h-[1.375rem] border-none pl-[0.313rem]"
                type="number"
              />
            </div>
          </div>
        </FilterRow>
        <FilterRow title="Wallet">
          <div className="group h-[1.375rem]">
            <Input
              value={sendAmount.wallet}
              leftSide={
                <GradientBorderRadius
                  radius="right"
                  gradient="input-50-min"
                  hover="input-50-left"
                  className="h-full w-[1.5rem]"
                >
                  <div className="w-full ml-[0.0625rem] h-full flex items-center justify-center">
                    <InputIcon iconName="solana" />
                  </div>
                </GradientBorderRadius>
              }
              placeholder="Address"
              onChange={(wallet) =>
                setSendAmount((sAmount) => ({
                  ...sAmount,
                  wallet,
                }))
              }
              className="h-[1.375rem] border-none pl-[0.313rem]"
            />
          </div>
        </FilterRow>
      </div>
      <Modal.Footer>
        <Button
          title="Confirm"
          className="w-[9.4375rem] font-semibold text-xs"
          onClick={handleSend}
          loading={isPending || isPendingNative}
          success={showSuccess}
          enabled={
            isConnected && !isPending && !isPendingNative && !showSuccess
          }
        />

        <Button
          title="Cancel"
          active={false}
          className="w-[9.4375rem] font-semibold text-xs"
          onClick={toggleOpen}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default TokenSendModal;
